import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import type { VocabularySet, VocabularySetProgress } from "@lyder-talk/types";
import { db } from "@lyder-talk/core/lib/db-client";
import { apiGet, apiPost } from "@/lib/api-client";

/**
 * Columns every vocabulary-set read shares.
 *
 * The embedded session is addressed by its own foreign-key column
 * (`!source_practice_session_id`) rather than by the constraint name, and it
 * cannot be aliased: the table gateway resolves `relation!column`, but rejects
 * PostgREST's `alias:relation!fk_constraint(…)` form outright. `normalizeVocabularySet`
 * re-keys the result below.
 */
export const VOCABULARY_SET_SELECT =
    "*, profiles!creator_id (display_name, avatar_url), practice_sessions!source_practice_session_id (topic, slug)";

/**
 * Re-key the embedded session to the `source_session` shape callers read.
 *
 * The embed keeps its table name because it cannot be aliased in the select
 * (see `VOCABULARY_SET_SELECT`), so the rename happens here exactly once.
 */
export function normalizeVocabularySet(row: Record<string, unknown> | null | undefined): VocabularySet | null {
    if (!row) return null;
    const { practice_sessions: sourceSession, ...rest } = row;
    return { ...(rest as unknown as VocabularySet), source_session: (sourceSession as VocabularySet["source_session"]) ?? null };
}

export interface VocabularySetsListParams {
    page?: number;
    pageSize?: number;
    search?: string;
    /** 'all' | 'mine' | 'public' | 'shared' | 'topics' */
    filter?: string;
    /** 'newest' | 'most_words' */
    sort?: string;
    ownerId?: string | null;
    /**
     * Skip the query. The sidebar renders this as a collapsed list, so it passes
     * its open state instead of calling `get_vocabulary_sets` on every page.
     */
    enabled?: boolean;
}

export interface VocabularySetsListResult {
    sets: VocabularySet[];
    total: number;
    totalPages: number;
}

export interface VocabularySetsOverview {
    count_all: number;
    count_mine: number;
    count_public: number;
    count_shared: number;
    count_topics: number;
    community_sets: number;
    words_available: number;
    my_collections: number;
    auto_from_topics: number;
    in_progress: number;
    owner_options: Array<{ id: string; name: string }>;
}

const fetchVocabularySets = async (
    params: Required<Omit<VocabularySetsListParams, "enabled">>
): Promise<VocabularySetsListResult> => {
    const { page, pageSize, search, filter, sort, ownerId } = params;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const query = new URLSearchParams({
        limit: String(pageSize),
        offset: String((page - 1) * pageSize),
    });
    if (search) query.set("search", search);
    if (filter) query.set("filter", filter);
    if (ownerId) query.set("ownerId", ownerId);
    if (sort) query.set("sort", sort);

    const rows = await apiGet<any[]>(`/v1/vocabulary-sets?${query.toString()}`);
    const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
    const sets: VocabularySet[] = rows.map((r) => ({
        id: r.id,
        title: r.title,
        description: r.description,
        share_code: r.share_code,
        word_count: r.word_count,
        creator_id: r.creator_id,
        created_at: r.created_at,
        is_public: r.is_public,
        is_auto_generated: r.is_auto_generated,
        source_practice_session_id: r.source_practice_session_id,
        profiles: { display_name: r.creator_display_name, avatar_url: r.creator_avatar_url },
        source_session: r.source_topic ? { topic: r.source_topic, slug: r.source_slug } : null,
    }));

    return { sets, total, totalPages: Math.ceil(total / pageSize) };
};

const fetchAutoGeneratedSets = async (limit = 6): Promise<VocabularySet[]> => {
    const { data, error } = await (db as any).from("vocabulary_sets")
        .select(VOCABULARY_SET_SELECT)
        .eq("is_auto_generated", true)
        .order("created_at", { ascending: false })
        .limit(limit);

    if (error) throw error;
    return (data ?? []).map(normalizeVocabularySet).filter(Boolean) as VocabularySet[];
};

const fetchTopicCollection = async (sessionId: string): Promise<VocabularySet | null> => {
    const { data, error } = await (db as any).from("vocabulary_sets")
        .select(VOCABULARY_SET_SELECT)
        .eq("is_auto_generated", true)
        .eq("source_practice_session_id", sessionId)
        .maybeSingle();

    if (error) throw error;
    return normalizeVocabularySet(data as Record<string, unknown> | null);
};

/**
 * Re-export kept for backward compatibility. The canonical definition now lives
 * in the central factory at `@/lib/query-keys` under `queryKeys.vocabularySets`.
 */
export const vocabularySetKeys = queryKeys.vocabularySets;

/**
 * Fetches a filtered/sorted PAGE of vocabulary sets the current user can see
 * (public sets + their own) via the `get_vocabulary_sets` RPC. Filtering,
 * sorting, search and pagination all happen server-side (was: fetch-all +
 * client-side compute in VocabularyCollections.tsx).
 */
export const useVocabularySets = (params: VocabularySetsListParams = {}) => {
    const {
        page = 1,
        pageSize = 24,
        search = "",
        filter = "all",
        sort = "newest",
        ownerId = null,
        enabled = true,
    } = params;
    return useQuery({
        queryKey: vocabularySetKeys.list(page, pageSize, search, filter, sort, ownerId),
        queryFn: () => fetchVocabularySets({ page, pageSize, search, filter, sort, ownerId }),
        enabled,
        staleTime: 30_000,
        placeholderData: (previousData) => previousData,
    });
};

/**
 * Overview aggregates for the collections page: filter-pill counts (search +
 * owner aware), stat cards (over all visible sets), in-progress count, and the
 * owner dropdown options — all in one `get_vocabulary_sets_overview` call.
 */
export const useVocabularySetsOverview = (
    search = "",
    ownerId: string | null = null
) =>
    useQuery({
        queryKey: vocabularySetKeys.overview(search, ownerId),
        queryFn: async (): Promise<VocabularySetsOverview> => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const overview = new URLSearchParams();
            if (search) overview.set("search", search);
            if (ownerId) overview.set("ownerId", ownerId);
            const row = await apiGet<Record<string, unknown>>(`/v1/vocabulary-sets/overview?${overview.toString()}`);
            return {
                count_all: Number(row.count_all ?? 0),
                count_mine: Number(row.count_mine ?? 0),
                count_public: Number(row.count_public ?? 0),
                count_shared: Number(row.count_shared ?? 0),
                count_topics: Number(row.count_topics ?? 0),
                community_sets: Number(row.community_sets ?? 0),
                words_available: Number(row.words_available ?? 0),
                my_collections: Number(row.my_collections ?? 0),
                auto_from_topics: Number(row.auto_from_topics ?? 0),
                in_progress: Number(row.in_progress ?? 0),
                owner_options: (row.owner_options ?? []) as Array<{ id: string; name: string }>,
            };
        },
        staleTime: 30_000,
        placeholderData: (previousData) => previousData,
    });

/**
 * Fetches the auto-generated vocabulary set for a given practice session
 * (topic). Returns `null` when the topic has no auto-set yet.
 */
export const useTopicCollection = (sessionId: string | null | undefined) =>
    useQuery({
        queryKey: vocabularySetKeys.topic(sessionId ?? ""),
        queryFn: () => fetchTopicCollection(sessionId as string),
        enabled: !!sessionId,
        staleTime: 30_000,
    });

/**
 * Fetches a small list of auto-generated sets for the Home "Featured topic
 * collections" row. Newest first.
 */
export const useFeaturedTopicCollections = (limit = 6) =>
    useQuery({
        queryKey: vocabularySetKeys.auto(limit),
        queryFn: () => fetchAutoGeneratedSets(limit),
        staleTime: 60_000,
    });

/**
 * Fetches per-user review progress for a batch of set ids. The result is keyed
 * by `set_id` for easy lookup at the call site.
 */
export const useVocabularySetsProgress = (
    setIds: string[] | undefined,
    enabled = true
) =>
    useQuery({
        queryKey: vocabularySetKeys.progress(setIds ?? []),
        queryFn: async () => {
            if (!setIds || setIds.length === 0) return [] as Array<{
                set_id: string;
                total_words: number;
                words_saved: number;
                words_reviewed: number;
                progress_percent: number;
            }>;
            const rows = await apiGet<Array<{
                set_id: string;
                total_words: number;
                words_saved: number;
                words_reviewed: number;
                progress_percent: number;
            }>>(`/v1/vocabulary-sets/progress?ids=${encodeURIComponent(setIds.join(","))}`);

            return rows as Array<{
                set_id: string;
                total_words: number;
                words_saved: number;
                words_reviewed: number;
                progress_percent: number;
            }>;
        },
        enabled: enabled && !!setIds && setIds.length > 0,
        staleTime: 30_000,
    });

export const buildProgressMap = (
    rows: Array<{
        set_id: string;
        total_words: number;
        words_saved: number;
        words_reviewed: number;
        progress_percent: number;
    }> | undefined
): Map<string, VocabularySetProgress> => {
    const map = new Map<string, VocabularySetProgress>();
    if (!rows) return map;
    for (const r of rows) {
        map.set(r.set_id, {
            total_words: r.total_words,
            words_saved: r.words_saved,
            words_reviewed: r.words_reviewed,
            progress_percent: r.progress_percent,
        });
    }
    return map;
};

export type { VocabularySet, VocabularySetProgress };
